import { ColumnDefinition } from 'pages/requestForm/selectors/types/ColumnDefinition';
import { useMemo } from 'react';

import { NetSuiteLineCustomFieldEditor } from '../../components';
import { getCustomFieldIsInvalid, sortCustomFields } from '../../helpers';
import { GenericCustomFieldsListItem, UseLineCustomFieldsParams } from './useLineCustomFields.types';

export const useLineCustomFields = (params: UseLineCustomFieldsParams) => {
    const {
        customFields,
        workflowId,
        sublistType,
        showErrors,
        showErrorsOnEmptyLine,
        ocrIsLoading = false,
        updateCustomField,
    } = params;

    const availableCustomFields = useMemo(() => {
        const filteredFields = customFields.filter((field) => {
            const isAppropriateLevel =
                field.netSuiteField?.level === 'Lines' || field.netSuiteField?.level === 'Header And Lines';

            return (
                !field.isArchived &&
                isAppropriateLevel &&
                field.workflows?.some((workflow) => workflow.workflowId === workflowId && workflow.isActive)
            );
        });

        return sortCustomFields(filteredFields);
    }, [customFields, workflowId]);

    const customFieldsColumnDefinition = useMemo(
        () =>
            availableCustomFields.map<ColumnDefinition<GenericCustomFieldsListItem>>((field) => ({
                id: field.id,
                name: field.name,
                readonly: () =>
                    !field.workflows?.some((workflow) => workflow.workflowId === workflowId && workflow.isEditable) ||
                    ocrIsLoading,
                invalid: (li) => {
                    if (showErrors && showErrorsOnEmptyLine) {
                        return true;
                    }

                    const fieldState = li.customFields?.find((customField) => customField.companyFieldId === field.id);

                    return showErrors && getCustomFieldIsInvalid(field, fieldState, workflowId);
                },
                cell: (li, { readonly }) => {
                    const fieldState = li.customFields?.find((customField) => customField.companyFieldId === field.id);

                    return (
                        <NetSuiteLineCustomFieldEditor
                            field={field}
                            fieldState={fieldState}
                            lineId={li.id}
                            sublistType={sublistType}
                            onChange={updateCustomField}
                            isReadonly={readonly}
                        />
                    );
                },
            })),
        [
            availableCustomFields,
            workflowId,
            sublistType,
            showErrors,
            showErrorsOnEmptyLine,
            ocrIsLoading,
            updateCustomField,
        ]
    );

    return {
        availableCustomFields,
        customFieldsColumnDefinition,
    };
};
